import { observable, action, computed, makeObservable } from 'mobx';
import _ from 'lodash';
import apiActions from 'api/actions';

export class SkillRecommendation {
  constructor(data, root) {
    this._root = root;
    this.id = data.id;
    this.parent_id = data.parent_id;
    this.potential_child_id = data.potential_child_id;
    this.skill_id = data.skill_id;
    this.power_skill_id = data.power_skill_id;
    this.recommended_by_id = data.recommended_by_id;
    this.learning_center_id = data.learning_center_id;
    this.user_message_id = data.user_message_id;
  }

  get parent() {
    return this._root.ParentStore.find(this.parent_id);
  }

  get potentialChild() {
    return this._root.PotentialChildStore.find(this.potential_child_id);
  }

  get academicSkill() {
    return this._root.SkillStore.find(this.skill_id);
  }

  get powerSkill() {
    return this._root.PowerSkillStore.findPowerSkill(this.power_skill_id);
  }

  get recommendedBy() {
    return this._root.UserStore.find(this.recommended_by_id);
  }

  get new() {
    return !_.find(this._root.ParentAttestationStore.all, ['skill_recommendation_id', this.id]);
  }

  get hasValidReferences() {
    if (this.skill_id) {
      return !!this.academicSkill;
    } else if (this.power_skill_id) {
      return !!this.powerSkill;
    }
    return false;
  }
}

export default class SkillRecommendationStore {
  constructor(root) {
    makeObservable(this, {
      data: observable,
      requests: observable,
      add: action,
      addMany: action,
      remove: action,
      all: computed,
      validRecommendations: computed,
      reset: action,
      fetchAll: action,
      refetch: action,
      fetchAllCompleted: computed
    })

    this.root = root;
  }

  data = new Map();
  requests = new Map();

  add = (data) => {
    const recommendation = new SkillRecommendation(data, this.root);
    this.data.set(recommendation.id, recommendation);
  }

  addMany = (datas) => {
    const merge = new Map();
    datas.forEach(data => {
      const recommendation = new SkillRecommendation(data, this.root);
      merge.set(recommendation.id, recommendation);
    })
    this.data.merge(merge);
  }

  remove = (id) => {
    if (id && this.data.has(id)) this.data.delete(id);
  }

  find(id) {
    return this.data.get(id);
  }

  get all() {
    return Array.from(this.data.values());
  }

  get validRecommendations() {
    return _.filter(this.all, 'hasValidReferences')
  }

  reset = () => {
    this.data.clear();
    this.requests.clear();
  }

  fetchAll() {
    if (this.requests.get('all') === undefined) {
      this.requests.set('all', 'pending');
      return apiActions.v2RawGet('users/me/skill_recommendations').then((response) => {
        this.addMany(response.data);
        this.requests.set('all', 'complete');
      })
    }
    return Promise.resolve();
  }

  refetch() {
    if (this.requests.set('all') !== undefined) {
      this.reset();
      return this.fetchAll();
    }
  }

  get fetchAllCompleted() {
    return this.requests.get('all') === 'complete';
  }

  getByParent(parentId) {
    return _.filter(this.validRecommendations, ['parent_id', parentId]);
  }

  newByParent(parentId) {
    return _.filter(this.getByParent(parentId), 'new');
  }

  getByChild(childId) {
    return _.filter(this.validRecommendations, ['potential_child_id', childId]);
  }

  newByChild(childId) {
    return _.filter(this.getByChild(childId), 'new');
  }

  forUserMessage(messageId) {
    return _.filter(this.validRecommendations, ['user_message_id', messageId]);
  }

  getByChildWithFilter(childId, filters) {
    let resultSet = this.getByChild(childId);
    if (filters.type && filters.type === 'academic') {
      resultSet = _.filter(resultSet, ['type', 'academic']);
      if (filters.subject) {
        resultSet = _.filter(resultSet, ['academicSkill.domain.subject.id', filters.subject]);
      }
      if (filters.domain) {
        resultSet = _.filter(resultSet, ['acdemicSkill.domain.id', filters.domain]);
      }
      if (filters.gradeLevel) {
        resultSet = _.filter(resultSet, ['academicSkill.domain.grade_level', `#{filters.gradeLevel}`]);
      }
    } else if (filters.type && filters.type === 'power') {
      resultSet = _.filter(resultSet, ['type', 'power']);
      if (filters.category) {
        resultSet = _.filter(resultSet, ['powerSkill.category.id', filters.category]);
      }
      if (filters.gradeBand) {
        resultSet = _.filter(resultSet, recommendation => (
          _.some(
            _.flatMap(recommendation.powerSkill.levels, 'parent_guidances'),
            ['grade.display', filters.gradeBand]
          )
        ))
      }
    }
    return resultSet;
  }
}
